import {
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { toFormData } from "axios";
import FileDropZone from "components/FileDropZone";
import SimpleBarScroll from "components/SimpleBar";
import SimpleBarHorizontal from "components/SimpleBarHorizontal";
import server_api from "data/server_apis/server_api";
import ProductImageUploadStatus from "pages/products/ProductImageUploadStatus";
import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteProductImageThunk,
  getProductThunk,
  productSelector,
  selectProduct,
} from "store/reducers/product/productSlice";
import addURLPrefix from "utils/addURLPrefix";

const ProductImages = ({ fullGrid = false, product = null, completed }) => {
  const [files, setFiles] = useState([]);
  const [fileStatus, setFileStatus] = useState([]);
  const [submitting, setSubMitting] = useState(false);
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => state.product.imageStatus);

  const uploadImages = () => {
    setSubMitting(true);
    const _fileStatus = files.map((file) => ({
      progress: 0,
      status: "pending",
    }));
    setFileStatus(_fileStatus);
    files.forEach((file, index) => {
      (async () => {
        const item = _fileStatus[index];
        const data = { product: product.id, image: file };
        try {
          const resp = await server_api.postForm(
            "/commodity/product-image",
            toFormData(data),
            {
              onUploadProgress: (progressEvent) => {
                const percentComplete = parseInt(
                  (progressEvent.loaded / progressEvent.total) * 100
                );
                item.progress = percentComplete;
                setFileStatus([..._fileStatus]);
              },
            }
          );

          item.status = "success";
          setFileStatus([..._fileStatus]);
        } catch (err) {
          item.status = "failed";
          setFileStatus([..._fileStatus]);
        }
      })();
    });

    if (completed) {
      completed();
    }

    dispatch(getProductThunk(product.id));
  };

  const removeImage = (image) => {
    dispatch(deleteProductImageThunk({ id: image, product: product }));
  };

  const _product = useSelector((state) =>
    productSelector.selectById(state, product?.id)
  );

  return (
    <>
      {_product.images.length > 0 && (
        <Stack padding={2} direction="column">
          <SimpleBarHorizontal>
            <Stack spacing={1} direction="row" marginBottom={2}>
              {product.images.map((item) => (
                <Paper key={item.id} padding={1} sx={{ width: 100 }}>
                  <Stack
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                  >
                    {loading?.delete === item.id ? (
                      <Skeleton
                        variant="rectangular"
                        sx={{ height: 100, width: 100 }}
                        animation="wave"
                      />
                    ) : (
                      <LazyLoadImage
                        width={100}
                        height={100}
                        src={addURLPrefix(item.image)}
                      />
                    )}

                    <Button
                      color="error"
                      size="small"
                      fullWidth
                      onClick={() => removeImage(item.id)}
                    >
                      Remove
                    </Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </SimpleBarHorizontal>
        </Stack>
      )}

      {!submitting && (
        <Grid container>
          <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
            <SimpleBarScroll sx={{ padding: 1 }}>
              <FileDropZone
                maxFiles={5}
                accept={{
                  "image/png": [".png"],
                  "image/jpeg": [".jpg", ".jpeg"],
                }}
                mode="image"
                files={files}
                setFiles={setFiles}
              />
            </SimpleBarScroll>
          </Grid>
        </Grid>
      )}

      {(files.length && !submitting) > 0 && (
        <Box display="flex" padding={1} justifyContent="center">
          <Button variant="outlined" size="small" onClick={uploadImages}>
            Upload images
          </Button>
        </Box>
      )}

      <Box
        padding={1}
        visibility={submitting ? "visible" : "hidden"}
        {...(!submitting && { display: "none" })}
      >
        <Typography color="text.secondary" variant="h5" marginBottom={1}>
          Uploading in progress
        </Typography>
        {files.map((item, index) => (
          <ProductImageUploadStatus
            file={item}
            key={index}
            progress={fileStatus[index]?.progress}
            status={fileStatus[index]?.status}
          />
        ))}
      </Box>
    </>
  );
};

export default ProductImages;
